import { useState } from 'react'
import { useGeometryStore } from '../../store/geometryStore'
import TemplateSelector from './TemplateSelector'
import CollectionForm from './CollectionForm'
import ReviewSubmit from './ReviewSubmit'
import type { TemplateJson } from '../../types/template'

type Step = 'select' | 'fill' | 'review'

export default function CollectionWorkflowModal() {
  const { isModalOpen, drawnGeometry, closeModal, clearGeometry } = useGeometryStore()
  const [step, setStep] = useState<Step>('select')
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [filledTemplate, setFilledTemplate] = useState<(TemplateJson & { _values: Record<string, unknown> }) | null>(null)

  if (!isModalOpen || !drawnGeometry) return null

  function handleClose() {
    closeModal()
    clearGeometry()
    setStep('select')
    setSelectedTemplate(null)
    setFilledTemplate(null)
  }

  const stepLabels: Record<Step, string> = {
    select: '1. Select Template',
    fill: '2. Fill Form',
    review: '3. Review & Submit',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-border bg-background shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur-sm px-6 py-4">
          <div className="flex items-center gap-2">
            {(['select', 'fill', 'review'] as Step[]).map((s, i) => (
              <span key={s} className="flex items-center gap-2">
                {i > 0 && <span className="text-muted-foreground/30">›</span>}
                <span className={`text-xs font-medium ${step === s ? 'text-primary' : 'text-muted-foreground'}`}>
                  {stepLabels[s]}
                </span>
              </span>
            ))}
          </div>
          <button
            onClick={handleClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'select' && (
            <TemplateSelector
              onSelect={(name) => {
                setSelectedTemplate(name)
                setStep('fill')
              }}
            />
          )}

          {step === 'fill' && selectedTemplate && (
            <CollectionForm
              templateName={selectedTemplate}
              onSubmit={(filled) => {
                setFilledTemplate(filled)
                setStep('review')
              }}
              onBack={() => setStep('select')}
            />
          )}

          {step === 'review' && filledTemplate && (
            <ReviewSubmit
              template={filledTemplate}
              drawnGeometry={drawnGeometry.geojson}
              onBack={() => setStep('fill')}
              onClose={handleClose}
            />
          )}
        </div>
      </div>
    </div>
  )
}
